import {
    Disclaimer,
    PassengerType,
    StaticCheckoutContent,
} from '@/api/Checkout/checkout.interfaces';
import {
    FerryProduct,
    MandatoryField,
    SerializationType,
    ProductType,
} from '@/api/Cart/cart.interfaces';
import {
    FerryPreAddon,
    FerryPreAddonType,
    Category,
    PreCheckoutData,
    ValidationType,
    ValueType,
} from '@shared/interfaces/preCheckout';
import { Market, Markets } from '@shared/config/market/market.models';
import { getCurrentMarketSpecs } from '@shared/config/market/market.helpers';

const market: Market = getCurrentMarketSpecs();
const Currency = market.moneyFormat.currency;

const terms = {
    Id: 'terms',
    Label: 'När du klickar "Genomför bokning" godkänner du våra',
    LinkText: 'Resevillkor',
    LinkUrl: 'https://www.sembo.se/pages/privacypolicy.aspx',
    IsCheckbox: ![Markets.SWEDEN, Markets.GERMANY].includes(market.number),
} as Disclaimer;

const privacyPolicy = {
    Id: 'privacyPolicy',
    Label: 'Vi sparar dina personuppgifter för att kunna hantera din bokning.',
    LinkText: 'Om insamling av personuppgifter',
    LinkUrl: 'https://www.sembo.se/pages/privacypolicy.aspx#why_we_collect_data',
    IsCheckbox: false,
} as Disclaimer;

const confirmation = {
    Id: 'confirmation',
    Label: 'Jeg bekræfter hermed at mine data er korrekte, samt at kurven indeholder de varer jeg ønsker.',
    LinkText: null,
    LinkUrl: null,
    IsCheckbox: true,
} as Disclaimer;

const insuranceConfirmation = {
    Id: 'insuranceConfirmation',
    Label: 'Jag har läst och godkänner förköpsinformation och fullständiga villkor och medger att jag har ett behov av att teckna aktuell försäkringsprodukt.',
    LinkText: null,
    LinkUrl: null,
    IsCheckbox: true,
} as Disclaimer;

const euServicePackageRights = {
    Id: 'euServicePackageRights',
    Label: 'De kombinerade resetjänster du erbjuds utgör en paketresa i den mening som avses i',
    LinkText: 'direktiv (EU) 2015/2302.',
    LinkUrl:
        'https://www.sembo.se/booking-flow-pages/infobox-included-in-booking-flow/packagetrips',
    IsCheckbox: false,
} as Disclaimer;

const getDisclaimers = (): Disclaimer[] => {
    const disclaimers: Disclaimer[] = [];

    disclaimers.push(terms);
    disclaimers.push(privacyPolicy);

    if (market.number === Markets.DTFDENMARK) {
        disclaimers.push(confirmation);
    }

    disclaimers.push(insuranceConfirmation);

    if (market.number === Markets.SWEDEN) {
        disclaimers.push(euServicePackageRights);
    }

    return disclaimers;
};

export const fakeStaticCheckoutContent: StaticCheckoutContent = {
    IsCaptumAvailable: false,
    Disclaimers: getDisclaimers(),
    GenderTitles: {
        1: 'Male',
        2: 'Female',
    },
    DiscountEnabled: true,
    Nationalities: [
        {
            CountryCode: 'dk',
            NationalityName: 'Dansk',
        },
        {
            CountryCode: 'fi',
            NationalityName: 'Finsk',
        },
        {
            CountryCode: 'se',
            NationalityName: 'Svensk',
        },
        {
            CountryCode: 'es',
            NationalityName: 'Spansk',
        },
        {
            CountryCode: 'ro',
            NationalityName: 'Rumänsk',
        },
    ],
    CurrentNationality: 'SE',
    PhoneNumberPrefixes: {
        prefixes: ['46', '34', '33'],
        selected: '46',
    },
    SpecialNeedsOptions: [
        {
            Name: 'Inget specialbehov',
            Value: 0,
        },
        {
            Name: 'Hörselskadad',
            Value: 1,
        },
        {
            Name: 'Synskadad',
            Value: 2,
        },
        {
            Name: 'Synskadad/hörselskadad',
            Value: 3,
        },
        {
            Name: 'Rullstolsburen',
            Value: 4,
        },
        {
            Name: 'Elektrisk rullstol',
            Value: 5,
        },
        {
            Name: 'Rollator',
            Value: 6,
        },
        {
            Name: 'Medicinering',
            Value: 8,
        },
        {
            Name: 'Behöver assistans',
            Value: 10,
        },
    ],
    AgeRangeDefinitions: {
        Flight: {
            Adult: {
                Min: 12,
                Max: 120,
                Type: PassengerType.Adult,
            },
            Child: {
                Min: 2,
                Max: 11,
                Type: PassengerType.Child,
            },
            Infant: {
                Min: 0,
                Max: 1,
                Type: PassengerType.Infant,
            },
        },
        Ferry: {
            Adult: {
                Min: 16,
                Max: 120,
                Type: PassengerType.Adult,
            },
            Child: {
                Min: 4,
                Max: 15,
                Type: PassengerType.Child,
            },
            Infant: {
                Min: 0,
                Max: 3,
                Type: PassengerType.Infant,
            },
        },
    },
};

const fakeFerryProduct1: FerryProduct = {
    $type: SerializationType.FerryProduct,
    Pax: [
        {
            PaxDescription: {
                IsAdult: true,
                Age: 99,
            },
            Title: 0,
            Gender: 0,
            FirstName: null,
            LastName: null,
            SpecialNeedsOption: null,
            DateOfBirth: null,
            Nationality: null,
            MandatoryFields: [
                MandatoryField.Gender,
                MandatoryField.FirstName,
                MandatoryField.LastName,
                MandatoryField.DateOfBirth,
                MandatoryField.Nationality,
            ],
        },
        {
            PaxDescription: {
                IsAdult: true,
                Age: 99,
            },
            Title: 0,
            Gender: 0,
            FirstName: null,
            LastName: null,
            SpecialNeedsOption: null,
            DateOfBirth: null,
            Nationality: null,
            MandatoryFields: [
                MandatoryField.Gender,
                MandatoryField.FirstName,
                MandatoryField.LastName,
                MandatoryField.DateOfBirth,
                MandatoryField.Nationality,
            ],
        },
    ],
    Legs: [
        {
            Segments: [
                {
                    DeparturePortCode: 'SEGOT',
                    DeparturePortName: 'Gothenburg',
                    DepartureLocationName: null,
                    ArrivalPortCode: 'DKFDH',
                    ArrivalPortName: 'Frederikshavn',
                    ArrivalLocationName: null,
                    FerryName: null,
                    Company: 'Stena Line',
                    TicketType: {
                        Name: 'ECONOMY',
                        Description:
                            'Ändring av linje, datum och avgångstid kan göras fram till avgång mot en ändringsavgift på 500 SEK per väg och tillfälle. Övriga ändringar – ingen avgift.. Ingen återbetalning vid avbokning.',
                    },
                    Vehicle: null,
                    Vehicles: null,
                    DepartureDate: '2023-06-06T15:45:00.0000000',
                    ArrivalDate: '2023-06-06T19:00:00.0000000',
                    CompanyLogo: null,
                    Accommodations: [],
                    OriginalAccommodationsIncludedInPrice: [],
                    Meals: [],
                    Pets: [],
                    Lounges: [],
                },
            ],
        },
        {
            Segments: [
                {
                    DeparturePortCode: 'DKFDH',
                    DeparturePortName: 'Frederikshavn',
                    DepartureLocationName: null,
                    ArrivalPortCode: 'SEGOT',
                    ArrivalPortName: 'Gothenburg',
                    ArrivalLocationName: null,
                    FerryName: null,
                    Company: 'Stena Line',
                    TicketType: {
                        Name: 'ECONOMY',
                        Description:
                            'Ändring av linje, datum och avgångstid kan göras fram till avgång mot en ändringsavgift på 500 SEK per väg och tillfälle. Övriga ändringar – ingen avgift.. Ingen återbetalning vid avbokning.',
                    },
                    Vehicle: '1x Car, max 2 m high, 6 m long',
                    Vehicles: null,
                    DepartureDate: '2023-06-20T23:40:00.0000000',
                    ArrivalDate: '2023-06-21T02:05:00.0000000',
                    CompanyLogo: null,
                    Accommodations: [],
                    OriginalAccommodationsIncludedInPrice: [],
                    Meals: [],
                    Pets: [],
                    Lounges: [],
                },
            ],
        },
    ],
    DiscountedPrice: {
        Amount: 968.0,
        Currency,
    },
    Id: '69492242-7670-4639-8f6b-c5f2dff1c5b2',
    CombinationId: null,
    Type: ProductType.Ferry,
    Description: 'Frederikshavn - Gothenburg',
    Price: {
        Amount: 968.0,
        Currency,
    },
    AppliedDiscount: null,
    Tags: [],
};
const fakeFerryProduct2: FerryProduct = {
    $type: SerializationType.FerryProduct,
    Pax: [
        {
            PaxDescription: {
                IsAdult: true,
                Age: 99,
            },
            Title: 0,
            Gender: 0,
            FirstName: null,
            LastName: null,
            SpecialNeedsOption: null,
            DateOfBirth: null,
            Nationality: null,
            MandatoryFields: [
                MandatoryField.Gender,
                MandatoryField.FirstName,
                MandatoryField.LastName,
                MandatoryField.DateOfBirth,
                MandatoryField.Nationality,
            ],
        },
        {
            PaxDescription: {
                IsAdult: true,
                Age: 99,
            },
            Title: 0,
            Gender: 0,
            FirstName: null,
            LastName: null,
            SpecialNeedsOption: null,
            DateOfBirth: null,
            Nationality: null,
            MandatoryFields: [
                MandatoryField.Gender,
                MandatoryField.FirstName,
                MandatoryField.LastName,
                MandatoryField.DateOfBirth,
                MandatoryField.Nationality,
            ],
        },
    ],
    Legs: [
        {
            Segments: [
                {
                    DeparturePortCode: 'SEGOT',
                    DeparturePortName: 'Gothenburg',
                    DepartureLocationName: null,
                    ArrivalPortCode: 'DKFDH',
                    ArrivalPortName: 'Frederikshavn',
                    ArrivalLocationName: null,
                    FerryName: null,
                    Company: 'Stena Line',
                    TicketType: {
                        Name: 'ECONOMY',
                        Description:
                            'Ändring av linje, datum och avgångstid kan göras fram till avgång mot en ändringsavgift på 500 SEK per väg och tillfälle. Övriga ändringar – ingen avgift.. Ingen återbetalning vid avbokning.',
                    },
                    Vehicle: null,
                    Vehicles: null,
                    DepartureDate: '2023-06-06T15:45:00.0000000',
                    ArrivalDate: '2023-06-06T19:00:00.0000000',
                    CompanyLogo: null,
                    Accommodations: [],
                    OriginalAccommodationsIncludedInPrice: [],
                    Meals: [],
                    Pets: [],
                    Lounges: [],
                },
            ],
        },
        {
            Segments: [
                {
                    DeparturePortCode: 'DKFDH',
                    DeparturePortName: 'Frederikshavn',
                    DepartureLocationName: null,
                    ArrivalPortCode: 'SEGOT',
                    ArrivalPortName: 'Gothenburg',
                    ArrivalLocationName: null,
                    FerryName: null,
                    Company: 'Stena Line',
                    TicketType: {
                        Name: 'FLEXITICKET',
                        Description: '',
                    },
                    Vehicle: '1x Car, max 2 m high, 6 m long',
                    Vehicles: null,
                    DepartureDate: '2023-06-20T23:40:00.0000000',
                    ArrivalDate: '2023-06-21T02:05:00.0000000',
                    CompanyLogo: null,
                    Accommodations: [],
                    OriginalAccommodationsIncludedInPrice: [],
                    Meals: [],
                    Pets: [],
                    Lounges: [],
                },
            ],
        },
    ],
    DiscountedPrice: {
        Amount: 968.0,
        Currency,
    },
    Id: '69492242-7670-4639-8f6b-c5f2dff1c5b2',
    CombinationId: null,
    Type: ProductType.Ferry,
    Description: 'Gothenburg - Frederikshavn',
    Price: {
        Amount: 968.0,
        Currency,
    },
    AppliedDiscount: null,
    Tags: [],
};
const fakeFerryProduct3: FerryProduct = {
    $type: SerializationType.FerryProduct,
    Pax: [
        {
            PaxDescription: {
                IsAdult: true,
                Age: 99,
            },
            Title: 0,
            Gender: 0,
            FirstName: null,
            LastName: null,
            SpecialNeedsOption: null,
            DateOfBirth: null,
            Nationality: null,
            MandatoryFields: [
                MandatoryField.Gender,
                MandatoryField.FirstName,
                MandatoryField.LastName,
                MandatoryField.DateOfBirth,
                MandatoryField.Nationality,
            ],
        },
        {
            PaxDescription: {
                IsAdult: true,
                Age: 99,
            },
            Title: 0,
            Gender: 0,
            FirstName: null,
            LastName: null,
            SpecialNeedsOption: null,
            DateOfBirth: null,
            Nationality: null,
            MandatoryFields: [
                MandatoryField.Gender,
                MandatoryField.FirstName,
                MandatoryField.LastName,
                MandatoryField.DateOfBirth,
                MandatoryField.Nationality,
            ],
        },
    ],
    Legs: [
        {
            Segments: [
                {
                    DeparturePortCode: 'DEKEL',
                    DeparturePortName: 'Kiel',
                    DepartureLocationName: null,
                    ArrivalPortCode: 'SEGOT',
                    ArrivalPortName: 'Gothenburg',
                    ArrivalLocationName: null,
                    FerryName: null,
                    Company: 'REVLRUxTRUdPVDA0OjQ1MDg6MTVTdGVuYSBMaW5l',
                    TicketType: {
                        Name: '',
                        Description: '',
                    },
                    Vehicle: 'Bil, max 2 m hög, 6 m lång',
                    Vehicles: null,
                    Accommodations: [
                        {
                            Title: '2 bäddshytt (insides, våningssängar) - Inkluderat i priset',
                            Quantity: 1,
                            Type: 1,
                            Price: {
                                Amount: 930.4128,
                                Currency,
                            },
                        },
                    ],
                    OriginalAccommodationsIncludedInPrice: [
                        {
                            Title: '2 bäddshytt (insides, våningssängar) - Inkluderat i priset',
                            Quantity: 1,
                            Type: 1,
                            Price: {
                                Amount: 930.4128,
                                Currency,
                            },
                        },
                    ],
                    Meals: [],
                    Pets: [],
                    Lounges: [],
                    DepartureDate: '2023-11-19T17:45:00.0000000',
                    ArrivalDate: '2023-11-20T09:15:00.0000000',
                    CompanyLogo: null,
                },
            ],
        },
    ],
    DiscountedPrice: {
        Amount: 2827.0,
        Currency,
    },
    Id: 'faa878d6-2980-41c4-b2d2-a3d860fb0e72',
    CombinationId: null,
    Type: ProductType.Ferry,
    Description: 'Kiel - Gothenburg',
    Price: {
        Amount: 2827.0,
        Currency,
    },
    AppliedDiscount: null,
    Tags: [],
};
const fakeMeal: FerryPreAddon = {
    Id: 'aadsiu2-872s8-f23as-joficz892',
    Type: FerryPreAddonType.Meal,
    ImageUrl: null,
    Products: [
        {
            Product: fakeFerryProduct1,
            Options: [
                {
                    Id: '2901',
                    Title: 'Breakfast €7.28 Per Adult',
                    Description: null,
                    Category: null,
                    Type: ValueType.Count,
                    Price: {
                        Amount: 7.28,
                        Currency,
                    },
                    Value: 0,
                    Worth: {
                        Value: 0,
                        Description: null,
                    },
                    Validations: [],
                    ConnectedOptions: null,
                    InfoMessages: [],
                },
                {
                    Id: '3041',
                    Title: 'Breakfast Buffet (Restaurant Taste €12.48 Per Adult',
                    Description: 'From 21:30:00 To 22:30:00',
                    Category: null,
                    Type: ValueType.Count,
                    Price: {
                        Amount: 12.48,
                        Currency,
                    },
                    Value: 0,
                    Worth: {
                        Value: 0,
                        Description: null,
                    },
                    Validations: [],
                    ConnectedOptions: null,
                    InfoMessages: ['From 21:30:00 To 22:30:00', 'Only for adults'],
                },
                {
                    Id: '1594',
                    Title: 'Buffet (Restaurant Food City) - €20.80 Per Person',
                    Description: 'No time constraints',
                    Category: null,
                    Type: ValueType.Count,
                    Price: {
                        Amount: 20.8,
                        Currency,
                    },
                    Value: 0,
                    Worth: {
                        Value: 0,
                    },
                    Validations: [],
                    ConnectedOptions: null,
                    InfoMessages: ['No time constraints'],
                },
            ],
            MandatoryState: null,
        },
        {
            Product: fakeFerryProduct2,
            Options: [],
            MandatoryState: null,
        },
    ],
};
const fakeAccommodations: FerryPreAddon[] = [
    {
        Id: 'aaasda2-872s8-f23as-joficz877',
        Type: FerryPreAddonType.Accommodation,
        ImageUrl: 'https://www.sembo.se/media/14557662/Frdhavn-GBG.jpg',
        Products: [
            {
                Product: fakeFerryProduct1,
                Options: [
                    {
                        Id: '17322',
                        Title: '5 berth cabin (Outside, Bunk Beds)',
                        Description: 'Minimum 2 Occupants',
                        ImageUrl: 'https://www.sembo.se/media/14638516/side-1.jpg',
                        Type: ValueType.Count,
                        Category: Category.Cabin,
                        Price: {
                            Amount: 799,
                            Currency,
                        },
                        Value: 0,
                        IncludedInBasePrice: 0,
                        Worth: {
                            Value: 5,
                        },
                        Validations: [
                            {
                                Value: 4,
                                ValidationType: ValidationType.Maximum,
                                Message: 'Det finns enbart 2 tillgängliga',
                            },
                        ],
                        ConnectedOptions: null,
                        InfoMessages: [
                            'Please note: this cabin is only for wheelchair/disabled users only, failure to comply will result in your cabin being re-allocated.',
                            'Minimum 2 Occupants',
                        ],
                    },
                    {
                        Id: '17321',
                        Title: 'Reserved Seat (Reclining Seat)',
                        Description: null,

                        Category: Category.Seat,
                        Type: ValueType.Count,
                        Price: {
                            Amount: 120,
                            Currency,
                        },
                        Value: 0,
                        Worth: {
                            Value: 1,
                        },
                        Validations: [
                            {
                                Value: 1,
                                ValidationType: ValidationType.Maximum,
                                Message: 'There is only 1 available',
                            },
                        ],
                        ConnectedOptions: null,
                        InfoMessages: [],
                    },
                    {
                        Id: '17323',
                        Title: '2 berth cabin (Shared, Outside, Female only)',
                        ImageUrl: null,
                        Description: 'Female Only',
                        Category: Category.Cabin,
                        Type: ValueType.Count,
                        Price: {
                            Amount: 295,
                            Currency,
                        },
                        Value: 0,
                        Worth: {
                            Value: 2,
                            Description: 'This cabin suits 2 people',
                        },
                        Validations: [
                            {
                                Value: 99,
                                ValidationType: ValidationType.Maximum,
                                Message: 'Det finns enbart 99 tillgängliga',
                            },
                        ],
                        ConnectedOptions: null,
                        InfoMessages: ['Female Only', 'Shared'],
                    },
                    {
                        Id: '17324',
                        Title: '2 berth cabin (Pet Friendly)',
                        ImageUrl: null,
                        Description: 'Max 2 Pets',
                        Category: Category.Cabin,
                        Type: ValueType.Count,
                        Price: {
                            Amount: 399,
                            Currency,
                        },
                        Value: 0,
                        Worth: {
                            Value: 2,
                            Description: 'This cabin suits 2 people and 2 pets',
                        },
                        Validations: [
                            {
                                Value: 4,
                                ValidationType: ValidationType.Maximum,
                                Message: 'Det finns enbart 4 tillgängliga',
                            },
                        ],
                        ConnectedOptions: null,
                        InfoMessages: ['Pets Allowed', 'Maximum 2 Occupants'],
                    },
                ],
                MandatoryState: {
                    Type: ValueType.Count,
                    Value: 4,
                    ValidationType: ValidationType.Minimum,
                    Message:
                        'Vänligen välj tillräckligt med hytter/sittplatser för antalet passagerare',
                },
            },
        ],
    },
    {
        Id: 'aaasda2-872s8-bbb12-joficz877',
        Type: FerryPreAddonType.Accommodation,
        ImageUrl: null,
        Products: [
            {
                Product: fakeFerryProduct3,
                Options: [
                    {
                        Id: '17321',
                        Title: 'Reserverat säte (vilosäte)',
                        Description: null,
                        Category: 1,
                        Price: {
                            Amount: 82.0,
                            Currency,
                        },
                        Type: ValueType.Count,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 1,
                            Description: 'Max en passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '15667',
                        Title: '2 bäddshytt (insides, våningssängar)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 582.0,
                            Currency,
                        },
                        Type: ValueType.Count,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 2,
                            Description: 'Max 2 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '15677',
                        Title: '2 bäddshytt (utsides, våningssängar)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 640.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 2,
                            Description: 'Max 2 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '1492',
                        Title: '4 bäddshytt (insides)',
                        Description: 'Minsta antal passagerare är 2',
                        Category: 0,
                        Price: {
                            Amount: 640.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 4,
                            Description: 'Max 4 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '12730',
                        Title: '4 bäddshytt (utsides)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 698.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 4,
                            Description: 'Max 4 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '17376',
                        Title: '2 bäddshytt (Deluxe klass)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 930.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 2,
                            Description: 'Max 2 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '25925',
                        Title: 'Sovpodd med 1 slaf, överslaf (Nycklar fås ombord)',
                        Description: null,
                        Category: 1,
                        Price: {
                            Amount: 233.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 1,
                            Description: 'Max en passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '25924',
                        Title: 'Sovpodd med 1 slaf, underslaf (Nycklar fås ombord)',
                        Description: null,
                        Category: 1,
                        Price: {
                            Amount: 233.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 1,
                            Description: 'Max en passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                ],
                OptionsIncludedInPrice: [],
                MandatoryState: {
                    Type: 0,
                    Value: 0,
                    ValidationType: 1,
                    Message:
                        'Vänligen välj tillräckligt med hytter/sittplatser för antalet passagerare',
                },
            },
            {
                Product: fakeFerryProduct3,
                Options: [
                    {
                        Id: '15667',
                        Title: '2 bäddshytt (insides, våningssängar)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 930.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 1,
                        IncludedInBasePrice: 1,
                        InfoMessages: [],
                        Worth: {
                            Value: 2,
                            Description: 'Max 2 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '341',
                        Title: '2 bäddshytt (insides)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 1163.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 2,
                            Description: 'Max 2 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '15677',
                        Title: '2 bäddshytt (utsides, våningssängar)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 1221.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 2,
                            Description: 'Max 2 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '8440',
                        Title: '3 bäddshytt (insides)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 1046.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 3,
                            Description: 'Max 3 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '1492',
                        Title: '4 bäddshytt (insides)',
                        Description: 'Minsta antal passagerare är 2',
                        Category: 0,
                        Price: {
                            Amount: 1221.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 4,
                            Description: 'Max 4 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '346',
                        Title: '2 bäddshytt (utsides)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 1453.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 2,
                            Description: 'Max 2 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '313',
                        Title: '3 bäddshytt (utsides)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 1337.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 3,
                            Description: 'Max 3 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '12730',
                        Title: '4 bäddshytt (utsides)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 1511.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 4,
                            Description: 'Max 4 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '27227',
                        Title: '4 bäddshytt (utsides, skylight suite)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 2034.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 4,
                            Description: 'Max 4 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '27228',
                        Title: '4 bäddshytt (utsides, skylight  round suite)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 2150.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 4,
                            Description: 'Max 4 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '27226',
                        Title: '4 bäddshytt (utsides, balkong)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 2383.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 4,
                            Description: 'Max 4 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '27225',
                        Title: '4 bäddshytt (utsides, panorama suite)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 2499.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 4,
                            Description: 'Max 4 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '27224',
                        Title: '4 bäddshytt (utsides, jacuzzi suite, panorama class)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 3254.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 4,
                            Description: 'Max 4 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '27229',
                        Title: '4 bäddshytt (utsides, panorama suite)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 3022.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [],
                        Worth: {
                            Value: 4,
                            Description: 'Max 4 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '20654',
                        Title: '4 bäddshytt (insides, handikappad)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 1221.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [
                            'Observera: den här stugan är endast tillgänglig för rullstolsburna användare, underlåtenhet att följa kommer att leda till att din stuga omfördelas.',
                        ],
                        Worth: {
                            Value: 4,
                            Description: 'Max 4 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                    {
                        Id: '335',
                        Title: '4 bäddshytt (utsides, handikappsanpassad)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 1511.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 0,
                        IncludedInBasePrice: 0,
                        InfoMessages: [
                            'Observera: den här stugan är endast tillgänglig för rullstolsburna användare, underlåtenhet att följa kommer att leda till att din stuga omfördelas.',
                        ],
                        Worth: {
                            Value: 4,
                            Description: 'Max 4 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                ],
                OptionsIncludedInPrice: [
                    {
                        Id: '15667',
                        Title: '2 bäddshytt (insides, våningssängar)',
                        Description: null,
                        Category: 0,
                        Price: {
                            Amount: 930.0,
                            Currency,
                        },
                        Type: 0,
                        Value: 1,
                        IncludedInBasePrice: 1,
                        InfoMessages: [],
                        Worth: {
                            Value: 2,
                            Description: 'Max 2 passagerare',
                        },
                        Validations: [],
                        ConnectedOptions: null,
                    },
                ],
                MandatoryState: {
                    Type: 0,
                    Value: 2,
                    ValidationType: 1,
                    Message:
                        'Vänligen välj tillräckligt med hytter/sittplatser för antalet passagerare',
                },
            },
        ],
    },
];
const fakePet: FerryPreAddon = {
    Id: 'aadsiu2-872s8-f23as-joficz666',
    Type: FerryPreAddonType.Pet,
    ImageUrl: null,
    Products: [
        {
            Product: fakeFerryProduct1,
            Options: [
                {
                    Id: '3001',
                    Title: 'Pets on Kennel',
                    Description: 'Ferry kennel',
                    Category: null,
                    Type: ValueType.CountSelect,
                    Price: {
                        Amount: 0,
                        Currency,
                    },
                    Value: 0,
                    Worth: {
                        Value: 0,
                        Description: null,
                    },
                    Validations: [],
                    ConnectedOptions: null,
                    InfoMessages: ['Your pet traveling in the ship kennel.'],
                },
                {
                    Id: '3002',
                    Title: 'Pets on Cabin',
                    Description: 'Must select a cabin adapted for pets',
                    Category: null,
                    Type: ValueType.Count,
                    Price: {
                        Amount: 0,
                        Currency,
                    },
                    Value: 0,
                    Worth: {
                        Value: 0,
                        Description: null,
                    },
                    Validations: [],
                    ConnectedOptions: null,
                    InfoMessages: [
                        'Selected Cabin adapted for pets is MANDATORY',
                        'Pets Allowed',
                    ],
                },
            ],
            MandatoryState: null,
        },
    ],
};

const fakeLounge: FerryPreAddon = {
    Id: 'bbsiu2-872s8-f23as-joficz5123',
    Type: FerryPreAddonType.Lounge,
    ImageUrl: null,
    Products: [
        {
            Product: fakeFerryProduct1,
            Options: [
                {
                    Id: '3001',
                    Title: 'Pluslounge - Plus Lounge, avskild lounge inkl snacks Fr 18 år',
                    Description: '',
                    Category: null,
                    Type: ValueType.Count,
                    Price: {
                        Amount: 149,
                        Currency,
                    },
                    Value: 0,
                    Worth: {
                        Value: 0,
                        Description: null,
                    },
                    Validations: [],
                    ConnectedOptions: null,
                    InfoMessages: [],
                },
            ],
            MandatoryState: null,
        },
    ],
};

export const fakePreCheckout: PreCheckoutData = {
    ProductGroups: {
        Ferry: {
            Addons: [...fakeAccommodations, fakeMeal, fakePet, fakeLounge],
        },
    },
    TotalPrice: {
        Amount: 2827.0,
        Currency,
    },
};
